import { getTransactions } from '../admin-api';
import type { TransactionRow } from '../admin-api';
import { esc, adminTable, pagination, searchBar, badge, formatDate, formatCents } from '../admin-ui';

export async function mountTransactions(container: HTMLElement): Promise<() => void> {
  let alive = true;
  let rows: TransactionRow[] = [];
  let page = 1;
  let totalPages = 1;
  let search = '';
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  async function load(): Promise<void> {
    if (!alive) return;
    try {
      const res = await getTransactions({ page, per_page: 25, search: search || undefined });
      rows = res.items;
      totalPages = Math.ceil(res.total / res.pageSize) || 1;
    } catch {
      container.innerHTML = '<p class="text-red-400 p-4">Failed to load transactions.</p>';
      return;
    }
    render();
  }

  function statusColor(s: string): 'green' | 'red' | 'yellow' | 'gray' {
    if (s === 'COMPLETED') return 'green';
    if (s === 'PENDING') return 'yellow';
    if (s === 'FAILED') return 'red';
    return 'gray';
  }

  function render(): void {
    const tableRows = rows.map(r => [
      formatDate(r.createdAt),
      esc(r.username ?? r.email ?? r.userId),
      // amountCents, not amount: core's `amount` is already major units (2026-10-19).
      formatCents(r.amountCents),
      esc(r.currency),
      esc(r.provider),
      badge(r.status, statusColor(r.status)),
    ]);

    container.innerHTML = `
      <h2 class="font-heading text-gold text-lg mb-4">Transactions</h2>
      <div class="mb-4">${searchBar('Search transactions...', 'adm-tx-search')}</div>
      ${adminTable(['Date', 'User', 'Amount', 'Currency', 'Provider', 'Status'], tableRows)}
      ${pagination(page, totalPages)}
    `;
    wire();
  }

  function wire(): void {
    const input = container.querySelector<HTMLInputElement>('#adm-tx-search');
    if (input) {
      input.value = search;
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          search = input.value.trim();
          page = 1;
          void load();
        }, 300);
      });
    }
    container.querySelectorAll<HTMLButtonElement>('[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        page = Number(btn.dataset.page);
        void load();
      });
    });
  }

  await load();
  return () => { alive = false; clearTimeout(debounceTimer); };
}
